import { useState, useRef, useEffect } from 'react';
import {
  Text,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  useDisclosure
} from '_shared/designSystem/components';
import { getHandednessColor } from '_shared/utils/playerUtil';
import PropTypes from 'prop-types';

export const ClickableHandedness = ({ handedness, playerName, playerId, onHandednessUpdate, disabled = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedHandedness, setSelectedHandedness] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuRef = useRef(null);

  const handednessOptions = ['LH', 'RH'];

  const handleHandednessClick = () => {
    if (!disabled) {
      setIsDropdownOpen(true);
    }
  };

  const handleHandednessSelect = (newHandedness) => {
    setSelectedHandedness(newHandedness);
    setIsDropdownOpen(false);
    onOpen();
  };

  const handleConfirmUpdate = () => {
    if (selectedHandedness && onHandednessUpdate) {
      onHandednessUpdate(playerId, selectedHandedness);
    }
    onClose();
    setSelectedHandedness(null);
  };

  const handleCancelUpdate = () => {
    onClose();
    setSelectedHandedness(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const displayHandedness = handedness || 'Unknown';
  const handednessColor = handedness && handedness !== 'Unknown' ? getHandednessColor(handedness) : 'red.500';

  return (
    <>
      <Box ref={menuRef} position="relative" display="inline">
        <Text
          as="span"
          color={handednessColor}
          cursor={disabled ? 'default' : 'pointer'}
          textDecoration="none"
          _hover={disabled ? {} : { color: 'primary.500' }}
          onClick={handleHandednessClick}
          title={disabled ? '' : 'Click to update handedness'}
          display="inline"
        >
          ({displayHandedness})
        </Text>

        {isDropdownOpen && (
          <Box
            position="absolute"
            top="100%"
            left="0"
            zIndex={1000}
            bg="white"
            border="1px solid"
            borderColor="grey.300"
            borderRadius="md"
            boxShadow="lg"
            minW="80px"
          >
            {handednessOptions.map((option) => (
              <Box
                key={option}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'grey.100' }}
                onClick={() => handleHandednessSelect(option)}
              >
                <Text fontSize="sm" color={getHandednessColor(option)}>
                  {option}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Confirmation Modal */}
      <HandednessConfirmationModal
        isOpen={isOpen}
        onClose={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
        playerName={playerName}
        selectedHandedness={selectedHandedness}
      />
    </>
  );
};

ClickableHandedness.propTypes = {
  handedness: PropTypes.string,
  playerName: PropTypes.string.isRequired,
  playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onHandednessUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

// Confirmation Modal Component
const HandednessConfirmationModal = ({ isOpen, onClose, onConfirm, playerName, selectedHandedness }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <Box p={6}>
          <Text fontSize="lg" fontWeight="semibold" mb={4}>
            Update Player Handedness
          </Text>
          <Text fontSize="md" color="grey.700" mb={6}>
            Are you sure you want to update {playerName}'s handedness to{' '}
            <Text as="span" color={getHandednessColor(selectedHandedness)} fontWeight="semibold">
              {selectedHandedness}
            </Text>
            ?
          </Text>
          <Flex gap={4} justify="center">
            <Text
              as="button"
              px={4}
              py={2}
              border="1px solid"
              borderColor="grey.300"
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: 'grey.100' }}
              onClick={onClose}
            >
              No
            </Text>
            <Text
              as="button"
              px={4}
              py={2}
              bg="primary.500"
              color="white"
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: 'primary.600' }}
              onClick={onConfirm}
            >
              Yes
            </Text>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};

HandednessConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  playerName: PropTypes.string.isRequired,
  selectedHandedness: PropTypes.string.isRequired
};
